// ADDED: the create form behind the Create button on the Multipacks page.
// Mirrors NewCarForm, with the fields a sealed pack actually has. The cars inside the
// pack are not recorded - only how many it holds.
import React, { useState } from 'react';

import axios, { getErrorMessage } from '../../api/axios';
import { fileToResizedDataUrl } from '../CarModels/NewCarForm';

// ADDED: the same validation approach as the car form, with the rules a pack needs.
const CURRENT_YEAR = new Date().getFullYear();

const VALIDATORS = {
    seriesId: (value) => {
        if (!value.trim()) return 'Series code is required.';
        if (value.trim().length > 32) return 'Series code must be 32 characters or fewer.';
        return '';
    },
    name: (value) => {
        if (!value.trim()) return 'Name is required.';
        if (value.trim().length < 2) return 'Name is too short.';
        if (value.length > 80) return 'Name must be 80 characters or fewer.';
        return '';
    },
    theme: (value) => (value.trim() ? (value.length > 80 ? 'Package series must be 80 characters or fewer.' : '') : 'Package series is required.'),
    datereleased: (value) => {
        if (!String(value).trim()) return 'Year is required.';
        const year = Number(value);
        if (!Number.isInteger(year)) return 'Year must be a whole number.';
        if (year < 1968 || year > CURRENT_YEAR + 1) return `Year must be between 1968 and ${CURRENT_YEAR + 1}.`;
        return '';
    },
    // MODIFIED: price is optional, same as on the car form. Blank saves as "not priced yet".
    price: (value) => {
        if (!String(value).trim()) return '';
        const price = Number(value);
        if (Number.isNaN(price)) return 'Price must be a number.';
        if (price < 0) return 'Price cannot be negative.';
        if (price > 100000) return 'That price looks wrong.';
        if (!/^\d*(\.\d{1,2})?$/.test(String(value).trim())) return 'Use at most two decimal places.';
        return '';
    },
};

export default function NewMultipackForm({ isVisible, onClose, onCreated }) {
    const [frontImage, setFrontImage] = useState(null);
    const [seriesId, setSeriesId] = useState('');
    const [name, setName] = useState('');
    const [theme, setTheme] = useState('');
    const [pieceCount, setPieceCount] = useState('5');
    const [datereleased, setDatereleased] = useState('');
    const [price, setPrice] = useState('');

    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});
    const [touched, setTouched] = useState({});

    const fields = [
        // MODIFIED: code kept in capitals, as printed on the package
        { id: 'seriesId', label: 'Series code', placeholder: 'Code on the package', value: seriesId, onChange: (value) => setSeriesId(value.toUpperCase()), caps: true },
        { id: 'name', label: 'Name', placeholder: 'Name on the package', value: name, onChange: setName },
        { id: 'theme', label: 'Package series', placeholder: 'Ex.: HW Race Team 5-Pack', value: theme, onChange: setTheme },
        { id: 'datereleased', label: 'Year released', placeholder: 'Ex.: 2024', value: datereleased, onChange: setDatereleased, type: 'number' },
        { id: 'price', label: 'Price (optional)', placeholder: 'Ex.: 9.99', value: price, onChange: setPrice, type: 'number', optional: true }, // MODIFIED
    ];

    // The common pack sizes, with a free-text fallback for the odd ones
    const pieceOptions = [2, 3, 4, 5, 6, 8, 9, 10, 12, 15, 20];

    const handleImageChange = async (event) => {
        const file = event.target.files[0];
        if (!file) return;
        try {
            setErrorMessage('');
            setFrontImage(await fileToResizedDataUrl(file));
        } catch (error) {
            setErrorMessage(error.message);
        }
    };

    const resetForm = () => {
        setFieldErrors({});
        setTouched({});
        setFrontImage(null);
        setSeriesId(''); setName(''); setTheme('');
        setPieceCount('5'); setDatereleased(''); setPrice('');
    };

    const submit = async (event) => {
        event.preventDefault();
        setErrorMessage('');
        setSuccessMessage('');

        // ADDED: every rule runs before anything is sent
        const values = { seriesId, name, theme, datereleased, price };
        const errors = Object.entries(VALIDATORS).reduce((all, [key, validate]) => {
            const message = validate(values[key] ?? '');
            if (message) all[key] = message;
            return all;
        }, {});

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            setTouched(Object.keys(VALIDATORS).reduce((all, key) => ({ ...all, [key]: true }), {}));
            setErrorMessage('Please fix the highlighted fields.');
            return;
        }

        setIsSubmitting(true);

        try {
            const response = await axios.post('/multipacks', {
                // MODIFIED: trimmed, so a stray space cannot create a near-duplicate entry
                seriesId: seriesId.trim(),
                name: name.trim(),
                theme: theme.trim(),
                pieceCount: Number(pieceCount),
                datereleased: Number(datereleased),
                price: price.trim() === '' ? null : Number(price), // MODIFIED: blank means not priced yet
                imageFront: frontImage,
            });

            setSuccessMessage(response.data.created
                ? 'Multipack added to the catalog.'
                : 'That multipack was already in the catalog.');
            resetForm();
            if (onCreated) onCreated();
        } catch (error) {
            setErrorMessage(getErrorMessage(error, 'Could not add the multipack.'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setErrorMessage('');
        setSuccessMessage('');
        onClose();
    };

    if (!isVisible) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="w-full max-w-xl cv-card relative max-h-[90vh] overflow-y-auto animate-fade-up">
                <div className="cv-stripe" />
                <button onClick={handleClose} aria-label="Close"
                    className="absolute top-4 right-3 cv-muted hover:text-brand-flame z-10 transition duration-300 text-3xl leading-none px-2">
                    &times;
                </button>

                <form className="px-4 sm:px-6 pb-6 pt-6" onSubmit={submit}>
                    <div className="text-center mb-5">
                        <p className="cv-eyebrow">New entry</p>
                        <h1 className="cv-display text-2xl sm:text-3xl">Add a multipack</h1>
                    </div>

                    {errorMessage && <p role="alert" className="cv-alert-error mb-4">{errorMessage}</p>}
                    {successMessage && <p className="cv-alert-success mb-4">{successMessage}</p>}

                    {/* Single photo: a sealed pack only really has a front */}
                    <div className="flex justify-center mb-5">
                        <div className="cv-well relative w-36 h-36 rounded-xl border-dashed flex items-center justify-center overflow-hidden transition duration-300 hover:border-brand-flame">
                            {frontImage
                                ? <img src={frontImage} alt="Pack front" className="w-full h-full object-contain" />
                                : <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8 cv-muted">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                                  </svg>}
                            <input type="file" accept="image/*" aria-label="Pack photo"
                                className="absolute inset-0 opacity-0 cursor-pointer" onChange={handleImageChange} />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
                        {fields.map((field) => (
                            <div key={field.id}>
                                <label className="cv-label" htmlFor={field.id}>{field.label}</label>
                                <input
                                    id={field.id}
                                    type={field.type || 'text'}
                                    step={field.id === 'price' ? '0.01' : undefined}
                                    placeholder={field.placeholder}
                                    autoComplete="off"
                                    required={!field.optional} // MODIFIED: was always required
                                    autoCapitalize={field.caps ? 'characters' : undefined} // ADDED
                                    value={field.value}
                                    onChange={(e) => {
                                        field.onChange(e.target.value);
                                        if (touched[field.id]) {
                                            setFieldErrors((previous) => ({ ...previous, [field.id]: VALIDATORS[field.id](e.target.value) }));
                                        }
                                    }}
                                    onBlur={(e) => {
                                        setTouched((previous) => ({ ...previous, [field.id]: true }));
                                        setFieldErrors((previous) => ({ ...previous, [field.id]: VALIDATORS[field.id](e.target.value) }));
                                    }}
                                    aria-invalid={Boolean(fieldErrors[field.id])}
                                    aria-describedby={fieldErrors[field.id] ? `${field.id}-error` : undefined}
                                    className="cv-input"
                                    style={fieldErrors[field.id] ? { borderColor: 'var(--accent)' } : undefined}
                                />
                                {fieldErrors[field.id] &&
                                    <p id={`${field.id}-error`} className="text-xs cv-accent mt-1">{fieldErrors[field.id]}</p>
                                }
                            </div>
                        ))}

                        <div>
                            <label className="cv-label" htmlFor="pieceCount">Cars in the pack</label>
                            <select id="pieceCount" value={pieceCount} required
                                onChange={(e) => setPieceCount(e.target.value)} className="cv-input">
                                {pieceOptions.map((count) => (
                                    <option key={count} value={count}>{count} cars</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 mt-6">
                        <button type="submit" disabled={isSubmitting} className="cv-btn cv-btn-primary flex-1 py-3 uppercase">
                            {isSubmitting ? 'Saving...' : 'Save'}
                        </button>
                        <button type="button" onClick={handleClose} className="cv-btn cv-btn-quiet flex-1 py-3">
                            Close
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
